import React, { useState } from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';

import get from 'lodash/get';
import noop from 'lodash/noop';

import { LoadingPanel } from '../components/common/LoadingPanel';
import { UploadEntities } from '../components/pipe-input/UploadEntities.view';
import { SourceEntities } from '../components/pipe-input/SourceEntities.view';
import { FetchedRemoteEntities } from '../components/pipe-input/FetchedRemoteEntities.view';
import { ExternalSourceEntities } from '../components/pipe-input/ExternalSourceEntities.view';
import { DatasetInspectorContainer } from '../components/dataset-inspector/DatasetInspectorContainer';

import type { RouteComponentProps, RouteComponent } from 'react-router';
import type { RootState } from '../types/state.types';
import type { Pipe, PipeConfigType } from '../types/pipes.types';

const PipeInput: React.FC<
  RouteComponentProps<{}, any> & {
    inputPipe?: Pipe;
    addCustomActions: () => void;
    removeCustomActions: () => void;
    registerRefresh: () => void;
    unregisterRefresh: () => void;
  }
> = ({
  params,
  inputPipe,
  addCustomActions = noop,
  removeCustomActions = noop,
  registerRefresh = noop,
  unregisterRefresh = noop,
}) => {
  const { pipeID } = params;

  const [fetchRemoteEntities, setFetchRemoteEntities] = useState(false);

  const pipe = useSelector((state: RootState) => inputPipe ?? state.pipes[pipeID]);

  if (!pipe) {
    return null;
  }

  const datasets = useSelector((state: RootState) => state.datasets);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);
  const datasetsLoaded = useSelector((state: RootState) => state.loadStatus.datasetsLoaded);

  const sourceType: PipeConfigType = get(pipe, 'config.effective.source.type');
  const subset = get(pipe, 'config.effective.source.subset');

  let description;
  let content;
  const sourceEntitiesTypes: Partial<PipeConfigType>[] = [
    'merge',
    'merge_datasets',
    'union_datasets',
    'diff_datasets',
    'embedded',
  ];

  if (sourceType === 'dataset' || sourceType === 'binary') {
    let datasetId;

    if (sourceType === 'dataset') {
      datasetId = get(pipe, 'config.effective.source.dataset');
    }

    if (sourceType === 'binary') {
      const url = get(pipe, 'config.effective.source.url');

      const splitUrl = url?.split('/') ?? '';

      if (splitUrl.includes('datasets')) {
        datasetId = splitUrl[splitUrl.indexOf('datasets') + 1];
      } else {
        datasetId = splitUrl[0];
      }
    }

    const dataset = datasetId ? datasets[datasetId] : undefined;

    if (!dataset) {
      if (datasetsLoaded) {
        description = <SourceEntities pipe={pipe} />;
        content = null;
      } else {
        content = <LoadingPanel />;
      }
    } else {
      content = (
        <DatasetInspectorContainer
          addCustomActions={addCustomActions}
          datasetId={datasetId}
          registerRefresh={registerRefresh}
          removeCustomActions={removeCustomActions}
          unregisterRefresh={unregisterRefresh}
          subset={subset}
          tab="input"
        />
      );
    }
  } else if (sourceType === 'http_endpoint') {
    description = <UploadEntities pipe={pipe} token={token} subUrl={subUrl} />;
    content = null;
  } else if (sourceEntitiesTypes.includes(sourceType)) {
    description = <SourceEntities pipe={pipe} />;
    content = null;
  } else {
    // In this case, we want to add a button that will explicitly load the entities instead of automatically loading them
    // This is because these other sources talk over the network and requests might count towards rate limits

    if (fetchRemoteEntities) {
      description = <FetchedRemoteEntities pipe={pipe} />;
      content = null;
    } else {
      description = <ExternalSourceEntities setFetchRemoteEntities={setFetchRemoteEntities} />;
      content = null;
    }
  }

  return (
    <>
      {description}
      {content}
    </>
  );
};

const Routed = withRouter(PipeInput as unknown as RouteComponent);

export { Routed as PipeInput };
